import { useEffect, useMemo, useState, useRef } from "react";
import {
    Box,
    Button,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Snackbar,
    Alert,
} from "@mui/material";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    TablePagination,
    InputAdornment
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import axiosInstance from '../axios.config';
import { FaEye } from "react-icons/fa";
import CloseIcon from "@mui/icons-material/Close";

export default function DocumentList({ category }) {
    const [documents, setDocuments] = useState([]);
    const [title, setTitle] = useState("");
    const [file, setFile] = useState(null);
    const fileInputRef = useRef(null);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedDocumentId, setSelectedDocumentId] = useState(null);

    const [openPdfDialog, setOpenPdfDialog] = useState(false);
    const [pdfUrl, setPdfUrl] = useState("");

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [totalCount, setTotalCount] = useState(0);

    const [pdfTitle, setPdfTitle] = useState("");

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "error",
    });

    const user = useMemo(
        () => JSON.parse(localStorage.getItem("userDetails") || "{}"),
        []
    );
    const isAdmin = user?.role === "Admin";

    const loadDocuments = async () => {
        console.log("Loading documents");
        console.log(category);

        const res = await axiosInstance.get(
            `/Document/category`,
            {
                params: {
                    category: category,
                    page,
                    pageSize: rowsPerPage,
                    search
                }
            }
        );


        setDocuments(res.data.data);
        setTotalCount(res.data.totalCount);
    };
    useEffect(() => {
        loadDocuments();
    }, [category, page, rowsPerPage, search]);

    const handleUpload = async () => {

        if (!title.trim()) {
            setSnackbar({
                open: true,
                message: "Please enter a document title.",
                severity: "warning",
            });
            return;
        }

        if (!file) {
            setSnackbar({
                open: true,
                message: "Please select a PDF file to upload.",
                severity: "warning",
            });
            return;
        }

        try {

            const formData = new FormData();
            formData.append("title", title);
            formData.append("category", category);
            formData.append("file", file);

            await axiosInstance.post("/Document/upload", formData);

            setSnackbar({
                open: true,
                message: "Document uploaded successfully.",
                severity: "success",
            });

            setTitle("");
            setFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            await loadDocuments();

        } catch (error) {

            setSnackbar({
                open: true,
                message: "Error uploading document.",
                severity: "error",
            });

        }
    };
    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/Document/${selectedDocumentId}`);

            setOpenDeleteDialog(false);
            setSelectedDocumentId(null);

            await loadDocuments();
        }
        catch (error) {
            console.error("Error deleting document:", error);
        }
    };

    const handleViewDocument = async (doc) => {
        try {
            const response = await axiosInstance.get(
                `/Document/viewdocument/${doc.documentID}`,
                {
                    responseType: "blob",
                }
            );

            const fileURL = URL.createObjectURL(
                new Blob([response.data], { type: "application/pdf" })
            );

            setPdfUrl(fileURL);
            setPdfTitle(doc.title);
            setOpenPdfDialog(true);

        } catch (error) {
            console.error("Error opening document:", error);
        }
    };

    return (
        <Box p={3}>
            <Typography variant="h4" gutterBottom>
                {category}
            </Typography>

            {isAdmin && (
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={3}
                        alignItems="center"
                    >
                        {/* Replaces the plain title <input> */}
                        <TextField
                            label="Document Title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            size="small"
                        />

                        {/* Replaces the native visible file input */}
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={<UploadFileIcon />}
                            sx={{
                                whiteSpace: "nowrap",
                                px: 3,
                                py: 1
                            }}
                        >
                            Choose PDF
                            <input
                                ref={fileInputRef}
                                hidden
                                type="file"
                                accept="application/pdf"
                                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                            />
                        </Button>

                        <Typography
                            variant="body2"
                            sx={{ minWidth: { sm: 180 } }}
                        >
                            {file ? file.name : "No file selected"}
                        </Typography>

                        <Button
                            variant="contained"
                            startIcon={<UploadFileIcon />}
                            onClick={handleUpload}
                            sx={{
                                px: 3,
                                py: 1
                            }}
                        >
                            Upload
                        </Button>
                    </Stack>
                </Paper>
            )}
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    justifyContent: "flex-end"
                }}
            >
                <TextField
                    size="small"
                    placeholder="Search documents..."
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>
            <Paper>
                <Table
                    sx={{
                        width: "100%",
                    }}
                >
                    <TableHead
                        sx={{ bgcolor: "grey.900" }}>
                        <TableRow>
                            <TableCell
                                align="left"
                                sx={{
                                    width: "60%",
                                    fontWeight: 600,
                                    color: "common.white",
                                }}
                            >
                                Title
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: "15%",
                                    fontWeight: 600,
                                    color: "common.white",
                                }}
                            >
                                Uploaded
                            </TableCell>

                            <TableCell
                                align="center"
                                sx={{
                                    width: "25%",
                                    fontWeight: 600,
                                    color: "common.white",
                                }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {documents.map((doc) => (
                            <TableRow key={doc.documentID} sx={{ "&:hover": { bgcolor: "action.hover" } }}>

                                <TableCell
                                    sx={{
                                        width: "70%",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {doc.title}
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "15%",
                                    }}
                                >
                                    {new Date(doc.uploadedDate).toLocaleDateString()}
                                </TableCell>

                                <TableCell
                                    align="center"
                                    sx={{
                                        width: "15%",
                                    }}
                                >
                                    <Box sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        justifyContent: "center",
                                        alignItems: "center",
                                        gap: 1
                                    }}>
                                        <Button
                                            size="small"
                                            sx={{
                                                minWidth: "70px"
                                            }}
                                            startIcon={<FaEye />}
                                            onClick={() => handleViewDocument(doc)}
                                        >
                                            View
                                        </Button>

                                        {isAdmin && (
                                            <Button
                                                color="error"
                                                size="small"
                                                sx={{
                                                    minWidth: "70px"
                                                }}
                                                startIcon={<DeleteIcon />}
                                                onClick={() => {
                                                    setSelectedDocumentId(doc.documentID);
                                                    setOpenDeleteDialog(true);
                                                }}
                                            >
                                                Delete
                                            </Button>

                                        )}
                                    </Box>
                                </TableCell>

                            </TableRow>
                        ))}
                    </TableBody>
                </Table>


                <Box
                    sx={{
                        borderTop: "1px solid",
                        borderColor: "divider"
                    }}
                >
                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(event, newPage) => {
                            setPage(newPage);
                        }}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        sx={{
                            ".MuiTablePagination-toolbar": {
                                display: "flex",
                                alignItems: "center",
                                minHeight: "52px",
                            },

                            ".MuiTablePagination-selectLabel": {
                                marginBottom: 0,
                            },

                            ".MuiTablePagination-displayedRows": {
                                marginBottom: 0,
                            },

                            ".MuiTablePagination-input": {
                                marginTop: 0,
                            },

                            ".MuiTablePagination-actions": {
                                display: "flex",
                                alignItems: "center",
                            },

                            ".MuiIconButton-root": {
                                padding: "6px",
                            }
                        }}
                    />
                </Box>

            </Paper>
            {/* Snackbar goes here */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() =>
                    setSnackbar({
                        ...snackbar,
                        open: false,
                    })
                }
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
            >
                <Alert
                    onClose={() =>
                        setSnackbar({
                            ...snackbar,
                            open: false,
                        })
                    }
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
            >
                <DialogTitle>
                    Delete Document
                </DialogTitle>

                <DialogContent>
                    Are you sure you want to delete this file?
                </DialogContent>

                <DialogActions>
                    <Button
                        onClick={() => setOpenDeleteDialog(false)}
                        color="inherit"
                    >
                        Cancel
                    </Button>

                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openPdfDialog}
                onClose={() => {
                    setOpenPdfDialog(false);
                    URL.revokeObjectURL(pdfUrl);
                    setPdfUrl("");
                    setPdfTitle("");
                }}
                fullWidth
                maxWidth={false}
                fullScreen={window.innerWidth < 600}
                PaperProps={{
                    sx: {
                        height: "90vh"
                    }
                }}
            >
                <DialogTitle>
                    {pdfTitle || "Document Preview"}

                    <IconButton
                        onClick={() => {
                            setOpenPdfDialog(false);
                            URL.revokeObjectURL(pdfUrl);
                            setPdfUrl("");
                            setPdfTitle("");
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    sx={{
                        p: 0,
                        height: "100%"
                    }}
                >
                    {pdfUrl && (
                        <iframe
                            src={`${pdfUrl}#toolbar=0`}
                            title="PDF Preview"
                            width="100%"
                            height="100%"
                            style={{
                                border: "none"
                            }}
                        />
                    )}
                </DialogContent>

            </Dialog>
        </Box>

    );
}

